<script lang="ts" setup>
import type { EditorMode } from "../interfaces";

export interface HTMLDocumentStylesWrapperProps {
  mode: EditorMode;
}

const props = defineProps<HTMLDocumentStylesWrapperProps>();
</script>

<template>
  <div class="html_doc" :class="{ [`html_doc--${props.mode}`]: true }">
    <slot />
  </div>
</template>

<style lang="scss">
$max_sign_height: 150px;

.html_doc {
  * {
    font-family: Arial;
    font-size: 1rem;
    line-height: $space-8;
  }

  span,
  strong,
  b,
  em,
  i {
    font-size: inherit;
  }

  ul,
  ol {
    @include sp.padding(l, 8);
  }

  ul {
    list-style: disc;
  }

  .ProseMirror {
    @include sp.padding(false, 2);
  }

  &__sign {
    @include sp.padding(x, 4);
    @include sp.margin(y, 2);
    @include border(1, solid, false);
    @include radius(s);
    @include colors.bg(semantic, "white");
    @include colors.border(content, mid, false);
    @include text.general(low, m);

    display: flex;
    align-items: center;
    justify-content: center;
    min-height: $max_sign_height;
    max-height: $max_sign_height;

    img {
      max-width: 100%;
      max-height: calc(140px - #{$space-4});
      height: auto;
      width: auto;
      object-fit: contain;
    }
  }

  &__input_field {
    @include sp.padding(y, 1);
    @include sp.padding(x, 2);
    @include sp.margin(x, 2);
    @include border(1, solid, false);
    @include radius(s);
    @include colors.bg(semantic, "white");
    @include colors.border(content, mid, false);
    @include text.general(low, m);

    input {
      min-width: 200px;
      width: 40%;
      max-width: 100%;
      user-select: none;
      pointer-events: none;
    }
  }

  &__checkbox {
    @include sp.padding(l, 7);
    @include sp.margin(b, 2);

    font-size: $space-4;
    display: flex;
    align-items: center;
    cursor: default;
    user-select: none;
    position: relative;

    input[type="checkbox"] {
      position: absolute;
      opacity: 0;
      cursor: default;

      &:checked ~ .checkmark {
        &::after {
          display: block;
        }
      }
    }

    .checkmark {
      @include colors.bg(content, low);
      @include radius(s);

      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      height: $space-5;
      width: $space-5;
      transition:
        background-color 0.2s,
        border-color 0.2s;

      &::after {
        content: "";
        position: absolute;
        display: none;
        left: calc(($space-5 - $space-2) / 2);
        top: calc((($space-5 - $space-3) - $space-1) / 2);
        width: $space-2;
        height: $space-3;
        border: solid white;
        border-width: 0 calc($space-1 / 2) calc($space-1 / 2) 0;
        transform: rotate(45deg);
      }
    }
  }

  &--complete {
    .html_doc {
      &__checkbox {
        .checkmark {
          cursor: pointer;
        }
      }

      &__sign {
        cursor: pointer;
      }

      &__input_field {
        input {
          cursor: text;
          pointer-events: all;
        }
      }
    }
  }
}
</style>
